import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useImagePicker } from '@/features/common/components/ImagePicker/useImagePicker';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useRemoveImageForExperience } from '@/features/experiences/hooks/useRemoveImageForExperience';
import { useUploadImageForExperience } from '@/features/experiences/hooks/useUploadImageForExperience';
import { ExperiencePrimaryImage } from '@/features/layouts/components/PrimaryImage';
import { Box, Button, Flex, Grid, Icon, MotionBox, Text, useToast } from '@/ui';
import { BannerRead } from './Banner.read';
import foreground from './foreground.png';
import type { BannerProps } from '.';

// Get placeholder image based on theme style
const getPlaceholderForTheme = (styleId: string | null | undefined) => {
  switch (styleId) {
    default:
      return foreground; // Default placeholder
  }
};

export const BannerWrite = ({ height, borderRadius }: BannerProps) => {
  // Resolve the radius once so the image and the hover overlay share it —
  // including the top-only radius ListHeader passes. Falls back to the card
  // radius (ExperiencePrimaryImage's own default) when unset.
  const resolvedRadius = borderRadius ?? 'var(--experience-card-radius)';
  const { experience } = usePageExperienceContext();
  const toast = useToast();
  const { open: openImagePicker } = useImagePicker();
  const { removeBannerImage, isPending: isRemovingImage } =
    useRemoveImageForExperience();
  const { uploadBannerImage, isPending: isUploadingImage } =
    useUploadImageForExperience();
  const [preview, setPreview] = useState<string | null>(
    experience?.bannerImageUrl ?? null
  );

  useEffect(() => {
    setPreview(experience?.bannerImageUrl ?? null);
  }, [experience?.bannerImageUrl]);

  const handleReplace = (file: File) => {
    const prevPreview = preview;

    uploadBannerImage(file, {
      onError: () => {
        toast.error('Error saving banner image');
        setPreview(prevPreview);
      },
    });
  };

  const handleRemove = () => {
    const prevPreview = preview;
    setPreview(null);

    removeBannerImage({
      onError: () => {
        toast.error('Error removing banner image');
        setPreview(prevPreview);
      },
    });
  };

  return (
    <AnimatePresence mode="popLayout">
      <MotionBox
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        key={String(experience?.theme?.bannerImageId)}
        transition={{ duration: 0.3 }}
      >
        {experience?.theme?.bannerImageId ? (
          <BannerRead borderRadius={borderRadius} height={height} />
        ) : (
          <ExperiencePrimaryImage
            borderRadius={resolvedRadius}
            data-group
            experience={experience}
            height={height}
            maxHeight={200}
            pos="relative"
            src={preview}
            placeholder={getPlaceholderForTheme(
              'default' // Replace with experience?.theme?.styleId if available
            )}
          >
            <Grid
              _groupHover={{ opacity: 1 }}
              alignItems="center"
              backdropFilter="blur(10px)"
              backgroundColor="rgba(255, 255, 255, 0.1)"
              borderRadius={resolvedRadius}
              h="100%"
              justifyItems="center"
              opacity="0"
              pos="absolute"
              templateRows="repeat(3, 1fr)"
              transition="opacity 0.1s ease-in-out"
              w="100%"
            >
              <Box />

              <Flex align="center" gap="2" justify="center">
                <Button
                  fontSize="md"
                  isDisabled={isRemovingImage}
                  isLoading={isUploadingImage}
                  leftIcon={<Icon icon="Upload" size="22px" />}
                  loadingText="Uploading..."
                  onClick={() => {
                    openImagePicker({
                      onSelect: (file) => {
                        const fileReader = new FileReader();
                        fileReader.onloadend = () =>
                          setPreview(fileReader.result as string);
                        if (file) {
                          fileReader.readAsDataURL(file);
                        }
                        handleReplace(file);
                      },
                    });
                  }}
                >
                  Replace
                </Button>

                {experience?.bannerImageUrl && (
                  <Button
                    fontSize="md"
                    isDisabled={isUploadingImage}
                    isLoading={isRemovingImage}
                    leftIcon={<Icon icon="X" size="22px" />}
                    loadingText="Removing..."
                    onClick={handleRemove}
                  >
                    Remove
                  </Button>
                )}
              </Flex>

              <Text
                bg="blackAlpha.500"
                borderRadius="lg"
                color="white"
                lineHeight="normal"
                px="2"
                py="1.5"
              >
                Recommended 1340px &times; 380px
              </Text>
            </Grid>
          </ExperiencePrimaryImage>
        )}
      </MotionBox>
    </AnimatePresence>
  );
};
